import React from 'react';
import { LayoutDashboard, Camera, Film, History, Settings, LogOut, Moon, Sun, X, Info, FileText, Globe, Thermometer, Printer, CloudRain, Sprout, Home, ThermometerSun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Avatar } from '../ui/Avatar';
import packageJson from '../../../package.json';

const SidebarItem = ({ icon: Icon, label, active, onClick }) => (
    <button
        onClick={onClick}
        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 group
      ${active
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            }`}
    >
        <Icon className={`w-5 h-5 transition-transform group-hover:scale-110 ${active ? 'stroke-[2.5px]' : ''}`} />
        <span className="font-medium text-sm">{label}</span>
    </button>
);

export const Sidebar = ({ activeTab, onTabChange, theme, toggleTheme, isOpen, onClose }) => {
    const { t, i18n } = useTranslation();
    const { user, logout } = useAuth();

    const menuItems = [
        { id: 'live', label: t('nav.live'), icon: Film },
        { id: 'timeline', label: t('nav.timeline'), icon: History },
        { id: 'temperature', label: t('nav.temperature'), icon: Thermometer },
        { id: 'sensors', label: t('nav.sensors'), icon: ThermometerSun },
        { id: 'weather', label: t('nav.weather'), icon: CloudRain },
        { id: 'printer', label: t('nav.printer'), icon: Printer },
        { id: 'jeanpaul', label: t('nav.jeanpaul'), icon: Sprout },
        { id: 'maison', label: t('nav.maison'), icon: Home },
        { id: 'dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
        { id: 'cameras', label: t('nav.cameras'), icon: Camera },

        ...(user?.role !== 'viewer' ? [{ id: 'settings', label: t('nav.settings'), icon: Settings }] : []),
        ...(user?.role === 'admin' ? [{ id: 'logs', label: t('nav.logs'), icon: FileText }] : []),
        { id: 'about', label: t('nav.about'), icon: Info },
    ];

    return (
        <aside
            className={`
            w-64 h-[100dvh] max-h-[100dvh] overflow-hidden bg-card border-r border-border flex flex-col fixed left-0 top-0 z-50
            transition-transform duration-300 ease-in-out
            ${isOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:translate-x-0
        `}
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
            <div className="p-6 flex flex-col items-center">
                <img
                    src="/logo.jpg"
                    alt="Olga"
                    className="h-20 w-20 lg:h-24 lg:w-24 rounded-full object-cover shadow-md ring-2 ring-primary/30"
                />
                <span className="mt-3 text-2xl font-bold tracking-tight bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
                    Olga
                </span>
                {/* Close button for mobile - absolute positioned */}
                <button
                    onClick={onClose}
                    className="lg:hidden absolute top-4 right-4 p-2 rounded-lg hover:bg-accent"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            <nav className="flex-1 overflow-y-auto min-h-0 px-4 space-y-1 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
                {menuItems.map((item) => (
                    <SidebarItem
                        key={item.id}
                        icon={item.icon}
                        label={item.label}
                        active={activeTab === item.id}
                        onClick={() => onTabChange(item.id)}
                    />
                ))}
            </nav>


            <div className="p-4 border-t border-border space-y-2">
                <button
                    onClick={() => onTabChange('profile')}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 group
                    ${activeTab === 'profile'
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                >
                    <Avatar user={user} size="sm" className="w-6 h-6 text-xs" />
                    <span className="font-medium text-sm truncate">{user?.username}</span>
                </button>

                <SidebarItem icon={LogOut} label={t('nav.logout')} onClick={logout} />

                <button
                    onClick={toggleTheme}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-200 group"
                >
                    {theme === 'dark' ? (
                        <Sun className="w-5 h-5 group-hover:text-yellow-500 transition-colors" />
                    ) : (
                        <Moon className="w-5 h-5 group-hover:text-blue-500 transition-colors" />
                    )}
                    <span className="font-medium text-sm">
                        {theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
                    </span>
                </button>

                <div className="w-full flex items-center space-x-3 px-4 py-2">
                    <Globe className="w-5 h-5 text-muted-foreground shrink-0" />
                    <div className="flex gap-1">
                        {['en', 'fr'].map(lang => (
                            <button
                                key={lang}
                                onClick={() => i18n.changeLanguage(lang)}
                                className={`px-2 py-0.5 rounded text-xs font-semibold uppercase transition-colors ${
                                    i18n.language === lang
                                        ? 'bg-primary/10 text-primary'
                                        : 'text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                {lang}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="pt-3 pb-6 lg:pb-1 flex flex-col items-center justify-center space-y-1">
                    <span className="text-xs text-muted-foreground font-mono tracking-wider">
                        v{packageJson.version}
                    </span>
                </div>
            </div>
        </aside>
    );
};
